import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BrandPanel } from "@/components/login/BrandPanel";
import { buildPublicHref, getSafeReturnToFromLocation } from "@/lib/return-to";
import { clearPendingBriefingContextOnAuthError } from "@/lib/briefing-context-bootstrap";

const ERROR_MESSAGES: Record<string, string> = {
  auth_failed: "auth_failed",
  not_approved:
    "This email is not approved for IntelHub access yet. Use the email your CyberFind invite approved, or contact your operator.",
  // Invite-token identity-binding deny reasons. These arrive as `?error=<code>`
  // when the binding gate refuses a sign-in. Inert until the
  // `binding_gate_enforced` flag is enabled at cutover.
  binding_required:
    "For your security, sign-in must start from your invitation. Please re-open your invite link from the email we sent you and try again.",
  token_expired:
    "Your invite link has expired. Ask your operator to send you a fresh invitation.",
  token_reused:
    "This invite link has already been used. If you still need access, ask your operator to send a new invitation.",
  email_mismatch:
    "This account doesn't match the identity provider your invitation is bound to. Sign in with the provider and email your invite was issued for, or contact your operator to re-bind your invitation.",
  csrf_nonce:
    "We couldn't verify your sign-in request. Please re-open your invite link and start again.",
  email_unverified:
    "Your identity provider did not return a verified email address. Verify your email with your provider, then try signing in again.",
};

const DEFAULT_ERROR = "Something went wrong. Please try again.";

const SOCIAL_PROVIDERS = [
  { idp: "microsoft", label: "Microsoft", icon: <MicrosoftIcon /> },
  { idp: "google", label: "Google", icon: <GoogleIcon /> },
  { idp: "linkedin", label: "LinkedIn", icon: <LinkedInIcon /> },
] as const;

function MicrosoftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="13" width="10" height="10" fill="#00a4ef" />
      <rect x="13" y="13" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
        fill="#0A66C2"
      />
    </svg>
  );
}

/**
 * Build the social sign-in URL for a provider, preserving a validated safe
 * return target. The backend contract is unchanged:
 * `/api/auth/login?idp=<provider>&returnTo=<encoded-safe-relative-path>`.
 */
export function buildSocialLoginUrl(
  idp: string,
  location: Pick<Location, "pathname" | "search"> = window.location,
): string {
  const returnTo = getSafeReturnToFromLocation(location);
  return `/api/auth/login?idp=${idp}&returnTo=${encodeURIComponent(returnTo)}`;
}

function SocialTile({
  icon,
  label,
  idp,
}: {
  icon: React.ReactNode;
  label: string;
  idp: string;
}) {
  const handleClick = () => {
    window.location.href = buildSocialLoginUrl(idp);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex min-h-24 flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-muted-foreground bg-card p-4 text-center transition-colors hover:border-primary hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      aria-label={`Continue with ${label}`}
    >
      {icon}
      <span className="text-xs font-medium text-muted-foreground">
        Continue with {label}
      </span>
    </button>
  );
}

export function LoginPage() {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");
    if (errorParam) {
      // KAN-661 R38 / AE12. `?error=` is the only local evidence that the IdP
      // round trip came back cancelled, failed or denied, so it is the only
      // place the pending briefing context can be released on that branch.
      // Every other terminal branch (expiry, story mismatch, sign-out, subject
      // change) is observable inside the bootstrap module itself.
      clearPendingBriefingContextOnAuthError(window.location.search);
      setError(ERROR_MESSAGES[errorParam] ?? DEFAULT_ERROR);
      // Strip ?error= from the URL so a refresh doesn't re-flash the banner.
      // Only `error` is deleted — any safe `returnTo` is preserved for retry.
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, []);

  // Computed at render: the safe target social sign-in and legal detours carry.
  const returnTo = getSafeReturnToFromLocation();

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background lg:grid-cols-2">
      {/* Auth surface first in DOM so mobile source/focus/SR order reaches the
          provider buttons before the (decorative) brand preview. On desktop the
          higher order value moves this column to the right of BrandPanel. */}
      <main className="flex items-center justify-center p-6 sm:p-10 lg:order-2">
        <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
          <div className="flex flex-col items-center gap-3">
            {/* CyberFind logo lockup. role=img + aria-label gives one stable
                accessible name across light/dark; the swapped <img>s are
                decorative. */}
            <span
              className="block text-center"
              role="img"
              aria-label="CyberFind"
              data-testid="cyberfind-brand-mark"
            >
              <img
                src="/icons/cyberfind-logo-light.svg"
                alt=""
                aria-hidden
                className="mx-auto h-11 w-auto dark:hidden"
              />
              <img
                src="/icons/cyberfind-logo-dark.svg"
                alt=""
                aria-hidden
                className="mx-auto hidden h-11 w-auto dark:block"
              />
            </span>
            <span className="text-2xl font-bold tracking-tight">IntelHub</span>
          </div>

          <p className="w-full rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-foreground">
            Invite-only access. Sign in with the provider linked to your
            CyberFind invitation.
          </p>

          {error && (
            <div
              role="alert"
              className="w-full rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error === "auth_failed" ? (
                <p>
                  We couldn't sign you in. Try again, or{" "}
                  <a
                    href="mailto:support@cyberfind.io"
                    className="font-medium underline"
                  >
                    contact support
                  </a>{" "}
                  if the problem persists.
                </p>
              ) : (
                <p>{error}</p>
              )}
            </div>
          )}

          <div className="flex w-full flex-col gap-3 sm:flex-row">
            {SOCIAL_PROVIDERS.map((provider) => (
              <SocialTile
                key={provider.idp}
                icon={provider.icon}
                label={provider.label}
                idp={provider.idp}
              />
            ))}
          </div>

          <nav
            aria-label="Legal"
            data-testid="login-footer"
            className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground"
          >
            <Link
              to={buildPublicHref("/privacy", returnTo)}
              className="hover:text-foreground"
            >
              Privacy
            </Link>
            <Link
              to={buildPublicHref("/terms", returnTo)}
              className="hover:text-foreground"
            >
              Terms
            </Link>
            <a
              href="mailto:support@cyberfind.io"
              className="hover:text-foreground"
            >
              Contact
            </a>
          </nav>
        </div>
      </main>

      <BrandPanel />
    </div>
  );
}
